/**
 * API Configuration Utilities
 *
 * Shared utilities for determining API base URLs across different services
 */

import { logger } from "./logger";

export type Environment = "labs" | "development" | "dev" | "staging" | "production";

// Primary domain per environment — used for URL construction
//
// "dev" and "development" are NOT aliases — they name two different physical
// places. "dev" is the org's remote GKE dev tier (Rails.env=dev is the
// convention our own apps report there); "development" is Rails' own default
// for something actually running on a developer's machine. A page that's
// really on the remote dev tier but reports "development" (e.g. an app that
// hasn't adopted the "dev" convention) is itself misconfigured — the fix is
// for that app to report "dev", not for the SDK to treat "development" as
// sometimes meaning "remote".
export const ENV_DOMAIN_MAP: Record<Environment, string> = {
  labs: "idmelabs.com",
  development: "idme.test",
  dev: "idme.dev",
  staging: "idme.cc",
  production: "id.me",
};

/**
 * Resolve the domain the analytics-api endpoint should be reached at for a given
 * environment. Labs is pinned to `idmelabs.co` unconditionally: analytics-api has
 * no `.com` deployment, so it stays on its own fixed domain independent of
 * ENV_DOMAIN_MAP/getSdkDomainForEnv below.
 */
export function getAnalyticsDomain(env: Environment): string {
  if (env === "labs") return "idmelabs.co";
  if (env === "development") return "idme.dev";
  return ENV_DOMAIN_MAP[env] ?? ENV_DOMAIN_MAP.production;
}

/**
 * Resolve the domain an SDK should build its iframe URL from, given the env the
 * host page passed to init(). An ENV_DOMAIN_MAP lookup — every environment,
 * including labs, resolves to a fixed domain regardless of the actual browser
 * hostname. No external partner integrating against labs can reach idmelabs.co
 * at all (it's internal/VPN-only), so there's nothing to detect: the public
 * idmelabs.com is the only domain a real consumer is ever on.
 *
 * Consumers pass a raw Rails/GKE env string through plain JS (the `Environment`
 * type is compile-time only), so an unrecognized value would otherwise resolve
 * to `undefined` and build a `websdk.undefined` host. Fall back to the production
 * domain and warn instead, so a misconfigured env degrades to a real host rather
 * than a silently broken one.
 */
export function getSdkDomainForEnv(env: Environment): string {
  const domain = ENV_DOMAIN_MAP[env];
  if (!domain) {
    logger.warn(
      `[IDMe SDK] unrecognized env "${env}" passed to init({ env }); ` +
        `falling back to the production host (${ENV_DOMAIN_MAP.production}).`,
    );
    return ENV_DOMAIN_MAP.production;
  }
  return domain;
}

// All domains per environment — single source of truth for validation and detection.
//
// "dev" and "development" are disjoint here too, matching ENV_DOMAIN_MAP:
// idme.dev belongs to "dev" (the remote tier), idme.test/localhost to
// "development" (a developer's own machine).
export const ENV_ALL_DOMAINS: Record<Environment, string[]> = {
  labs:        ["idmelabs.co", "idmelabs.com"],
  development: ["idme.test", "localhost"],
  dev:         ["idme.dev"],
  staging:     ["idme.cc"],
  production:  ["id.me", "idme.co"],
};

/**
 * Returns a flat list of all allowed domains across all environments.
 * Use for origin validation in hooks and routes.
 */
export function getAllowedDomains(): string[] {
  return (Object.values(ENV_ALL_DOMAINS) as string[][]).flat();
}

/**
 * Detect the current runtime environment from the browser hostname.
 * Used instead of VITE_ENV since a single Docker image serves all environments.
 */
export function getEnvironmentFromHostname(): string {
  if (typeof window === "undefined") return "unknown";
  const hostname = window.location.hostname;
  for (const [env, domains] of Object.entries(ENV_ALL_DOMAINS) as [Environment, string[]][]) {
    if (domains.some(domain => hostname === domain || hostname.endsWith(`.${domain}`))) {
      return env;
    }
  }
  return hostname;
}

/**
 * Get API base URL using environment mapping or domain extraction
 *
 * Two modes:
 * 1. Check if IDmeWebSDK.init({ env }) was called → use ENV_DOMAIN_MAP
 * 2. Auto-detect from current URL domain
 *
 * @param localPort - Port to use for local development (default: 8080)
 * @returns API base URL
 */
export function getApiBaseUrl(localPort: number = 8080): string {
  const apiPrefix = import.meta.env.VITE_API_BASE_URL_PREFIX || "api";

  // Check if SDK was initialized with an environment
  const sdkEnv = (window as any).IDmeWebSDK?._config?.env as Environment | undefined;

  if (sdkEnv && ENV_DOMAIN_MAP[sdkEnv]) {
    // Mode 1: Use environment mapping
    return `https://${apiPrefix}.${getSdkDomainForEnv(sdkEnv)}`;
  }

  // Mode 2: Auto-detect from current URL
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  const baseDomain = parts.slice(-2).join('.');

  // Local development uses localhost with specified port
  if (baseDomain === 'idme.test') {
    return `http://localhost:${localPort}`;
  }

  // All other environments use PREFIX + domain
  return `https://${apiPrefix}.${baseDomain}`;
}

/**
 * Get verification API base URL
 * Uses port 8091 for local development (hydra envoy proxy, avoids conflict with puma-dev on 8080)
 */
export function getVerificationApiBaseUrl(): string {
  return getApiBaseUrl(8091);
}

/**
 * Get consent API base URL
 * Uses port 8081 for local development
 */
export function getConsentApiBaseUrl(): string {
  return getApiBaseUrl(8081);
}

/**
 * Get legal ID verification (legal-ids) API base URL
 * Uses port 8086 for local development (athena envoy proxy)
 */
export function getLegalIdVerificationApiBaseUrl(): string {
  return getApiBaseUrl(8086);
}

export function getSsnCardVerificationApiBaseUrl(): string {
  return getApiBaseUrl(8086);
}
